import { useEffect, useRef } from 'react';

interface UseIncomingCallAlertsOptions {
  /** Truthy when there is an active incoming call */
  active: boolean;
  callerName?: string;
}

/**
 * Alerts the agent to an incoming call beyond the Twilio SDK's own ringtone:
 *  1. Fires a browser Notification. Permission is requested on the first user
 *     gesture: Safari and Firefox ignore a request made without one, and
 *     Chrome may quietly block it.
 *  2. Blinks the tab title so the agent notices even if the tab is in the background.
 */
export function useIncomingCallAlerts({
  active,
  callerName,
}: UseIncomingCallAlertsOptions): void {
  const originalTitleRef = useRef(document.title);

  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'default') return;

    let requesting = false;

    function removeListeners(): void {
      document.removeEventListener('pointerup', requestPermission);
      document.removeEventListener('keydown', requestPermission);
    }

    // Not every event that reaches here counts as a user gesture to the
    // browser (Escape, a modifier key), and a request made without one is
    // ignored. Keep listening until the permission has actually been decided.
    function stopOnceDecided(): void {
      requesting = false;
      if (Notification.permission !== 'default') removeListeners();
    }

    function requestPermission(): void {
      if (requesting) return;
      if (Notification.permission !== 'default') {
        removeListeners();
        return;
      }
      requesting = true;
      try {
        // Older Safari takes a callback and returns nothing.
        const request = Notification.requestPermission(stopOnceDecided) as
          | Promise<unknown>
          | undefined;
        if (request?.then) request.then(stopOnceDecided, stopOnceDecided);
      } catch {
        // Best-effort; the title blink still works without notifications
        requesting = false;
      }
    }

    document.addEventListener('pointerup', requestPermission);
    document.addEventListener('keydown', requestPermission);

    return removeListeners;
  }, []);

  useEffect(() => {
    if (!active) return;

    // Capture the current title before we start blinking
    originalTitleRef.current = document.title;

    let notification: Notification | null = null;
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        notification = new Notification('Incoming Call', {
          body: callerName ? `Call from ${callerName}` : 'You have an incoming call',
          tag: 'incoming-call',
          requireInteraction: true,
        });
        const shown = notification;
        shown.onclick = () => {
          window.focus();
          shown.close();
        };
      } catch {
        // Some browsers (Chrome on Android) only allow notifications from a
        // service worker and throw here; the title blink still runs.
      }
    }

    // Blink tab title
    let showAlert = true;
    const titleInterval = setInterval(() => {
      document.title = showAlert ? 'Incoming Call...' : originalTitleRef.current;
      showAlert = !showAlert;
    }, 1000);

    // Runs when the alert becomes inactive (answered, declined, cancelled),
    // when the caller name changes, and on unmount.
    return () => {
      clearInterval(titleInterval);
      document.title = originalTitleRef.current;
      notification?.close();
    };
  }, [active, callerName]);
}
